import { Project, ProrataInformation } from '@gameonly/core';
import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useUser, { isUserSubscribed } from '../authentication/context/useUser';
import CompleteQuestion from '../cnc-reports/CompleteQuestion';
import useReports from '../cnc-reports/context/useReports';
import CreateReportInformation from '../cnc-reports/CreateReportInformation';
import ProrataConfiguration from '../cnc-reports/ProrataConfiguration';

type StepKey = 'COMPLETE_QUESTION' | 'PRORATA_CONFIGURATION' | 'REPORT_INFO';

type Step = {
  label: string;
  next: ((input: string) => StepKey | null) | (() => null);
};

export interface ReportInfo {
  name: string;
  type: string;
  studio: string;
}

const STEP_TREE: Record<StepKey, Step> = {
  REPORT_INFO: {
    label: 'cnc-reports:creation-modal.information-step',
    next: () => 'COMPLETE_QUESTION' as StepKey,
  },
  COMPLETE_QUESTION: {
    label: 'cnc-reports:create-modal-local.complete-question',
    next: (isComplete: string) =>
      isComplete === 'complete' ? null : 'PRORATA_CONFIGURATION',
  },
  PRORATA_CONFIGURATION: {
    label: 'cnc-reports:create-modal-internal.prorata-config',
    next: () => null,
  },
};

const CreateReport = ({
  project,
  open,
  onClose,
}: {
  project: Project;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['project', 'common', 'cnc-reports']);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const navigate = useNavigate();
  const [isComplete, setIsComplete] = useState<string>('');
  const [reportValues, setReportValues] = useState<ReportInfo | undefined>(
    undefined,
  );
  const [activeStep, setActiveStep] = useState<StepKey>('REPORT_INFO');
  const { create, loadingCreate } = useReports();

  const createReport = async (prorataInformation?: ProrataInformation) => {
    if (!reportValues) return;
    const result = await create({
      ...reportValues,
      prorataInformation,
      projectId: project?._id,
    });
    navigate(`/cnc-reports/${result._id}`);
  };

  const handleNext = (payload: any) => {
    if (activeStep) {
      const step = STEP_TREE[activeStep];
      const nextStep = step.next(isComplete);
      if (activeStep === 'REPORT_INFO') {
        setReportValues(payload as ReportInfo);
      }
      if (nextStep) {
        setActiveStep(nextStep);
      } else {
        if (isComplete) {
          createReport();
        }
      }
    }
  };

  const handleClose = () => {
    setIsComplete('');
    setReportValues(undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('cnc-reports:creation-modal.title')}</DialogTitle>
      <DialogContent>
        <p className="hxb">{t(STEP_TREE[activeStep].label)}</p>

        {activeStep === 'REPORT_INFO' && (
          <Box mt={2}>
            <CreateReportInformation
              onNext={handleNext}
              onClose={handleClose}
              project={project}
              validationButtonLabel={'common:next'}
            />
          </Box>
        )}

        {activeStep === 'COMPLETE_QUESTION' && (
          <Box mt={2}>
            <CompleteQuestion
              subscribed={isUserSubscribed(user)}
              isComplete={isComplete}
              setIsComplete={setIsComplete}
              onClose={handleClose}
              onNext={handleNext}
              loading={loadingCreate}
              onNextLabel={
                isComplete === 'complete'
                  ? t('common:validate')
                  : t('common:next')
              }
            />
          </Box>
        )}

        {activeStep === 'PRORATA_CONFIGURATION' && (
          <Box mt={2}>
            <ProrataConfiguration
              onClose={handleClose}
              onSave={createReport}
              loading={loadingCreate}
              project={project}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateReport;
